import React, { useEffect, useState } from "react"
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"
import { parseISO, startOfMonth, endOfMonth } from 'date-fns'

import FilterSearch from 'pages/parts/FilterSearch'
import Pagination, { ItemPerPage } from 'pages/parts/Pagination'
import useDidMountEffect from 'pages/parts/useDidMountEffect'

import { GET, PGET } from 'helpers/api'
import { checkRole, formatDate, formatDateBackend } from 'helpers/helpers'

import Preview from './Preview'
import CreateExpense from './CreateExpense'
import EditExpense from './EditExpense'
import CancelExpense from './CancelExpense'
import { EXPENSE_STATUSES, currencyLabel, expenseAccountName, expenseStatus, expenseTotal, formatAmount, formatTime, responsibleName, sortTotals } from './financeUtils'
import './style.css'

const initialFilter = {
	posId: null,
	startDate: formatDateBackend(startOfMonth(new Date())),
	endDate: formatDateBackend(endOfMonth(new Date())),
	search: '',
}

function Index() {
	const URL = '/services/web/api/expense-list'
	const { t } = useTranslation()

	const canCreate = checkRole('EXPENSE_CREATE')
	const canEdit = checkRole('EXPENSE_EDIT')
	const canCancel = checkRole('EXPENSE_CANCEL')

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({ ...initialFilter, page: 0, random: 0 })
	const [ItemsPerPage, setItemsPerPage] = useState(20)
	const [pageCount, setPageCount] = useState(0)
	const [totals, setTotals] = useState([])
	const [data, setData] = useState([])
	const [previewItem, setPreviewItem] = useState(null)
	const [showPreview, setShowPreview] = useState(false)
	const [showCreate, setShowCreate] = useState(false)
	const [editItem, setEditItem] = useState(null)
	const [showEdit, setShowEdit] = useState(false)
	const [cancelItem, setCancelItem] = useState(null)
	const [showCancel, setShowCancel] = useState(false)
	const [poses, setPoses] = useState([])

	const posOptions = poses.map(item => ({ value: item.id, label: item.name }))

	// period is filtered by the backend: startDate/endDate (yyyy-MM-dd)
	function periodParams(filter) {
		const params = {}
		if (filter.startDate) params.startDate = filter.startDate
		if (filter.endDate) params.endDate = filter.endDate
		return params
	}

	async function getData() {
		checkFilter(filterData)
		const response = await PGET(URL, { ...filterData, size: ItemsPerPage }, { loader: true })
		if (response?.data) {
			const totalCount = Number(response.headers?.['x-total-count'])
			setPageCount(Number.isFinite(totalCount) ? Math.ceil(totalCount / ItemsPerPage) : 0)
			setData(response.data)
			setDropdownIsOpen(false)
		}
	}

	async function paginate(data) {
		const response = await PGET(URL, { ...filterData, page: data.selected, size: ItemsPerPage }, { loader: true })
		if (response) {
			setFilterData({ ...filterData, page: data.selected })
			setPageCount(Math.ceil(response.headers['x-total-count'] / ItemsPerPage))
			setData(response.data)
		}
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (Array.isArray(response)) {
			setPoses(response)
		}
	}

	// expense-total follows the POS and period filters, not the search
	async function getTotals(filter = filterData) {
		const params = periodParams(filter)
		if (filter.posId) params.posId = filter.posId
		const response = await GET('/services/web/api/expense-total', params)
		if (Array.isArray(response)) {
			setTotals(response)
		}
	}

	function posName(posId) {
		return poses.find(item => item.id === posId)?.name
	}

	function applyFilter() {
		setFilterData({ ...filterData, page: 0, random: filterData.random + 1 })
	}

	function checkFilter(filter) {
		setFilterDataExist(Object.keys(initialFilter).some(key => filter[key] !== initialFilter[key]))
	}

	function resetFilter() {
		setFilterData({ ...initialFilter, page: 0, random: filterData.random + 1 })
	}

	function openPreview(item) {
		setPreviewItem(item)
		setShowPreview(true)
	}

	function onCreated() {
		setShowCreate(false)
		getData()
		getTotals()
	}

	function openEdit(item) {
		setEditItem(item)
		setShowEdit(true)
	}

	function openCancel(item) {
		setCancelItem(item)
		setShowCancel(true)
	}

	function onEdited() {
		setShowEdit(false)
		getData()
		getTotals()
	}

	function onCanceled() {
		setShowCancel(false)
		getData()
		getTotals()
	}

	useEffect(() => {
		getPoses()
		getData()
		getTotals()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		getData()
		getTotals()
	}, [filterData.random, ItemsPerPage]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('FINANCIAL_OPERATIONS')}
				</h4>
				{canCreate &&
					<div className="d-flex">
						<button type="button" className="btn-rounded btn btn-primary" onClick={() => setShowCreate(true)}>
							<i className="uil uil-plus me-2"></i>
							{t('CREATE')}
						</button>
					</div>
				}
			</div>

			<div className="card">
				<div className="card-body">
					<ul className="nav nav-tabs mb-3">
						<li className="nav-item">
							<button type="button" className="nav-link active">
								<i className="uil uil-arrow-up-right me-1"></i>
								{t('EXPENSES')}
							</button>
						</li>
					</ul>

					<div className="d-flex flex-wrap mb-3">
						<div>
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}
								reduxFilterSearch={true}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
									{t('FILTER')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('PERIOD')}</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.startDate ? parseISO(filterData.startDate) : ''}
													onChange={(date) => setFilterData({ ...filterData, 'startDate': date ? formatDateBackend(date) : null })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-6">
											<label className="invisible">.</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.endDate ? parseISO(filterData.endDate) : ''}
													onChange={(date) => setFilterData({ ...filterData, 'endDate': date ? formatDateBackend(date) : null })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-12">
											<label>{t('POS')}</label>
											<Select
												options={posOptions}
												value={posOptions.find(option => option.value === filterData.posId) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'posId': option ? option.value : null })}
												placeholder={t('ALL')}
												isClearable
												noOptionsMessage={() => t('LIST_IS_EMPTY')}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end">
												<button type="button" className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
												<button type="button" className="btn btn-primary" onClick={() => applyFilter()}>{t('APPLY')}</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>

						<div className="d-flex fs-5 ms-auto">
							<span className="fw-700 me-2">{t('GRAND_TOTAL')}:</span>
							<div className="text-end">
								{sortTotals(totals).map(total => (
									<div key={total.currencyId}>{formatAmount(total.amount, currencyLabel(total.currencyId, total.currencyName))}</div>
								))}
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block mb-3">
							<div className="filter">
								<strong>{t('FILTER')}</strong>
								<br />
								{(filterData.startDate || filterData.endDate) &&
									<span className="me-2">
										<b>{t('PERIOD')}: </b>
										{filterData.startDate && formatDate(parseISO(filterData.startDate))}
										{' - '}
										{filterData.endDate && formatDate(parseISO(filterData.endDate))}
									</span>
								}
								{filterData.posId &&
									<span className="me-2">
										<b>{t('POS')}: </b>
										{posName(filterData.posId)}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('SEARCH')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
							</div>
						</div>
					}

					<ItemPerPage itemsPerPage={ItemsPerPage}
						setItemsPerPage={(e) => {
							setItemsPerPage(e)
							setFilterData({ ...filterData, page: 0, random: filterData.random + 1 })
						}}
					/>

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th>№</th>
									<th>{t('DATE')}</th>
									<th>{t('POS')}</th>
									<th>{t('TYPE')}</th>
									<th>{t('AMOUNT')}</th>
									<th>{t('ACCOUNT')}</th>
									<th>{t('RESPONSIBLE')}</th>
									<th>{t('STATUS')}</th>
									<th className="text-center">{t('ACTION')}</th>
								</tr>
							</thead>
							<tbody>
								{data.length > 0 ? data.map(item => {
									const status = expenseStatus(item)
									const canceled = status === 'CANCELED'
									const hasActions = !canceled && (canEdit || canCancel)
									return (
										<tr key={item.rowNumber} className={(showPreview && previewItem?.rowNumber === item.rowNumber) ? 'table-tr-active' : ''}>
											<td>{item.rowNumber}</td>
											<td>
												<div>{formatDate(item.createdDate)}</div>
												<div className="text-muted text-sm">{formatTime(item.createdDate)}</div>
											</td>
											<td>{item.posName}</td>
											<td>
												<div className="fw-600">{t('FO_OTHER_EXPENSE')}</div>
												<div className="text-muted text-sm">{item.expenseHelperName}</div>
											</td>
											<td className={'fw-600 text-nowrap' + (canceled ? ' text-muted text-decoration-line-through' : '')}>
												{formatAmount(expenseTotal(item), currencyLabel(item.currencyId, item.currencyName))}
											</td>
											<td>
												<div>{expenseAccountName(item, t)}</div>
												
											</td>
											<td>{responsibleName(item)}</td>
											<td>
												<span className={'fo-status ' + EXPENSE_STATUSES[status].className}>{t(EXPENSE_STATUSES[status].i18n)}</span>
											</td>
											<td>
												<div className="d-flex justify-content-center">
													<Dropdown align="end">
														<Dropdown.Toggle variant="" className="fo-action-toggle" title={t('ACTION')}>
															<i className="uil uil-ellipsis-h"></i>
														</Dropdown.Toggle>
														{/* portal to body: the menu must not be clipped by .table-responsive */}
														{createPortal(
															<Dropdown.Menu className="fo-action-menu">
																<Dropdown.Item as="button" onClick={() => openPreview(item)}>
																	<i className="uil uil-eye me-2"></i>
																	{t('MORE_DETAILS')}
																</Dropdown.Item>
																{!canceled && canEdit &&
																	<Dropdown.Item as="button" disabled={!item.isShiftOpen} onClick={() => openEdit(item)}>
																		<i className="uil uil-pen me-2"></i>
																		{t('EDIT')}
																	</Dropdown.Item>
																}
																{!canceled && canCancel &&
																	<Dropdown.Item as="button" className={item.isShiftOpen ? 'text-danger' : ''} disabled={!item.isShiftOpen} onClick={() => openCancel(item)}>
																		<i className="uil uil-ban me-2"></i>
																		{t('FO_CANCEL')}
																	</Dropdown.Item>
																}
																{/* edit and cancel need the expense's shift to be open */}
																{hasActions && !item.isShiftOpen &&
																	<>
																		<Dropdown.Divider />
																		<div className="fo-action-note text-muted">
																			<i className="uil uil-lock me-1"></i>
																			{t('FO_SHIFT_CLOSED_NOTE')}
																		</div>
																	</>
																}
															</Dropdown.Menu>,
															document.body
														)}
													</Dropdown>
												</div>
											</td>
										</tr>
									)
								}) : <tr>
										<td colSpan="9" className="text-center">{t('NO_DATA')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={filterData.page}></Pagination>
				</div>
			</div>

			<Preview show={showPreview} item={previewItem} onHide={() => setShowPreview(false)} />

			<CreateExpense show={showCreate} onHide={() => setShowCreate(false)} onSaved={onCreated} />

			<EditExpense show={showEdit} item={editItem} onHide={() => setShowEdit(false)} onSaved={onEdited} />

			<CancelExpense show={showCancel} item={cancelItem} onHide={() => setShowCancel(false)} onCanceled={onCanceled} />
		</>
	)
}

export default Index
